import { useState, useEffect, useCallback } from "react";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { toast } from "sonner";
import { FileSpreadsheet, TrendingUp, BarChart3, Loader2 } from "lucide-react";

import { DataUploadTab } from "./measure/DataUploadTab";
import { BaselineTab } from "./measure/BaselineTab";
import { ControlChartTab } from "./measure/ControlChartTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MeasurePhase({ projectId, token }) {
  // Core state
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  // Analysis results
  const [controlChart, setControlChart] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [columnClassification, setColumnClassification] = useState(null);
  const [denominatorColumn, setDenominatorColumn] = useState("");

  // Derived metrics
  const [derivedMetrics, setDerivedMetrics] = useState([]);
  const [showCreateDerivedModal, setShowCreateDerivedModal] = useState(false);
  const [selectedDerivedMetric, setSelectedDerivedMetric] = useState(null);

  // Capability analysis
  const [capabilityOpen, setCapabilityOpen] = useState(false);
  const [uslInput, setUslInput] = useState("");
  const [lslInput, setLslInput] = useState("");
  const [capabilityLoading, setCapabilityLoading] = useState(false);
  const [capabilityResult, setCapabilityResult] = useState(null);
  const [performanceResult, setPerformanceResult] = useState(null);
  const [sigmaShift, setSigmaShift] = useState(false);

  // Financial impact
  const [financialResult, setFinancialResult] = useState(null);
  const [annualVolume, setAnnualVolume] = useState("");
  const [costPerDefect, setCostPerDefect] = useState("");
  const [targetCpk, setTargetCpk] = useState("");
  const [useLongTerm, setUseLongTerm] = useState(false);
  const [financialLoading, setFinancialLoading] = useState(false);

  // AI states
  const [aiGenerating, setAiGenerating] = useState("");
  const [aiDataCollectionPlan, setAiDataCollectionPlan] = useState(null);
  const [aiMeasurementReview, setAiMeasurementReview] = useState(null);
  const [aiBaselineSuggestions, setAiBaselineSuggestions] = useState(null);

  // ---- Persistence ----
  const saveMeasureSettings = useCallback(async (overrides = {}) => {
    try {
      const payload = {
        selected_dataset_id: overrides.selected_dataset_id ?? selectedDataset?.dataset_id ?? "",
        selected_column: overrides.selected_column ?? selectedColumn ?? "",
        selected_derived_metric_id: overrides.selected_derived_metric_id ?? selectedDerivedMetric?.metric_id ?? "",
        usl: overrides.usl ?? uslInput ?? "",
        lsl: overrides.lsl ?? lslInput ?? "",
        sigma_shift: overrides.sigma_shift ?? sigmaShift,
        annual_volume: overrides.annual_volume ?? annualVolume ?? "",
        cost_per_defect: overrides.cost_per_defect ?? costPerDefect ?? "",
        target_cpk: overrides.target_cpk ?? targetCpk ?? "",
        use_long_term: overrides.use_long_term ?? useLongTerm,
      };
      await fetch(`${API}/projects/${projectId}/measure/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
    } catch { /* silent */ }
  }, [projectId, token, selectedDataset, selectedColumn, selectedDerivedMetric, uslInput, lslInput, sigmaShift, annualVolume, costPerDefect, targetCpk, useLongTerm]);

  const fetchDerivedMetrics = useCallback(async (datasetId) => {
    if (!datasetId) { setDerivedMetrics([]); return []; }
    try {
      const res = await fetch(`${API}/projects/${projectId}/datasets/${datasetId}/derived-metrics`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setDerivedMetrics(data); return data; }
    } catch { /* ignore */ }
    setDerivedMetrics([]);
    return [];
  }, [projectId, token]);

  // ---- Initial load with settings restore ----
  useEffect(() => {
    let cancelled = false;
    async function init() {
      try {
        const dsRes = await fetch(`${API}/projects/${projectId}/datasets`, { headers: { Authorization: `Bearer ${token}` } });
        if (!dsRes.ok || cancelled) return;
        const allDatasets = await dsRes.json();
        setDatasets(allDatasets);

        const settingsRes = await fetch(`${API}/projects/${projectId}/measure/settings`, { headers: { Authorization: `Bearer ${token}` } });
        const settings = settingsRes.ok ? await settingsRes.json() : {};

        let targetDs = null;
        if (settings.selected_dataset_id) targetDs = allDatasets.find((d) => d.dataset_id === settings.selected_dataset_id);
        if (!targetDs && allDatasets.length > 0) targetDs = allDatasets[0];

        if (targetDs) {
          setSelectedDataset(targetDs);
          const dms = await fetchDerivedMetrics(targetDs.dataset_id);
          if (settings.selected_column) {
            setSelectedColumn(settings.selected_column);
            if (settings.selected_derived_metric_id) {
              const dm = dms.find((m) => m.metric_id === settings.selected_derived_metric_id);
              if (dm) setSelectedDerivedMetric(dm);
            }
          }
        }

        if (settings.usl) setUslInput(String(settings.usl));
        if (settings.lsl) setLslInput(String(settings.lsl));
        if (settings.sigma_shift) setSigmaShift(true);
        if (settings.annual_volume) setAnnualVolume(String(settings.annual_volume));
        if (settings.cost_per_defect) setCostPerDefect(String(settings.cost_per_defect));
        if (settings.target_cpk) setTargetCpk(String(settings.target_cpk));
        if (settings.use_long_term) setUseLongTerm(true);

        if (targetDs && settings.selected_column) {
          const qs = new URLSearchParams({ dataset_id: targetDs.dataset_id, column: settings.selected_column });
          const resultsRes = await fetch(`${API}/projects/${projectId}/measure/last-results?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
          if (resultsRes.ok && !cancelled) {
            const lastResults = await resultsRes.json();
            if (lastResults.baseline) setBaseline(lastResults.baseline);
            if (lastResults.control_chart) {
              setControlChart(lastResults.control_chart);
              if (lastResults.control_chart.capability) setCapabilityResult(lastResults.control_chart.capability);
              if (lastResults.control_chart.performance) setPerformanceResult(lastResults.control_chart.performance);
              if (lastResults.control_chart.financial_impact) setFinancialResult(lastResults.control_chart.financial_impact);
            }
          }
        }
      } catch (error) {
        console.error("Failed to initialize measure phase:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, [projectId, token, fetchDerivedMetrics]);

  // ---- Refresh datasets (post-upload) ----
  const refreshDatasets = useCallback(async () => {
    try {
      const response = await fetch(`${API}/projects/${projectId}/datasets`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setDatasets(data);
        if (data.length > 0 && !selectedDataset) {
          setSelectedDataset(data[0]);
          fetchDerivedMetrics(data[0].dataset_id);
        }
      }
    } catch (error) { console.error("Failed to refresh datasets:", error); }
  }, [projectId, token, selectedDataset, fetchDerivedMetrics]);

  // ---- Upload handler ----
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${API}/projects/${projectId}/datasets/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Upload failed"); }
      const data = await response.json();
      toast.success(`Dataset uploaded: ${data.row_count} rows, ${data.columns.length} columns`);
      refreshDatasets();
    } catch (error) { toast.error(error.message); } finally { setUploading(false); event.target.value = ""; }
  };

  // ---- Analysis functions ----
  const classifyColumn = async (columnName) => {
    if (!selectedDataset || !columnName) { setColumnClassification(null); return; }
    const dm = derivedMetrics.find((m) => m.metric_id === columnName);
    setSelectedDerivedMetric(dm || null);
    try {
      const qs = new URLSearchParams({ column: columnName });
      if (dm) qs.set("derived_metric_id", dm.metric_id);
      const res = await fetch(`${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/classify-column?${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) setColumnClassification(await res.json());
    } catch { setColumnClassification(null); }
  };

  const generateControlChart = async () => {
    if (!selectedDataset || !selectedColumn) return;
    setAnalyzing(true);
    try {
      const body = { column: selectedColumn, dataset_id: selectedDataset.dataset_id };
      if (selectedDerivedMetric) body.derived_metric_id = selectedDerivedMetric.metric_id;
      if (denominatorColumn && !selectedDerivedMetric) body.denominator_column = denominatorColumn;
      const response = await fetch(`${API}/projects/${projectId}/analyze/control-chart`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed to generate control chart"); }
      const data = await response.json();
      setControlChart(data);
      setCapabilityResult(null); setPerformanceResult(null); setFinancialResult(null);
      toast.success("Control chart generated");
      saveMeasureSettings();
    } catch (error) { toast.error(error.message); } finally { setAnalyzing(false); }
  };

  const calculateBaseline = async () => {
    if (!selectedDataset || !selectedColumn) return;
    setAnalyzing(true);
    try {
      const body = { column: selectedColumn, dataset_id: selectedDataset.dataset_id };
      if (selectedDerivedMetric) body.derived_metric_id = selectedDerivedMetric.metric_id;
      const response = await fetch(`${API}/projects/${projectId}/analyze/baseline`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Failed to calculate baseline"); }
      const data = await response.json();
      if (data.results) { setBaseline(data.results); toast.success("Baseline calculated"); }
      else { setBaseline(data); toast.success("Baseline calculated"); }
    } catch (error) { toast.error(error.message); } finally { setAnalyzing(false); saveMeasureSettings(); }
  };

  const runCapabilityAnalysis = async () => {
    if (!controlChart || (!uslInput.trim() && !lslInput.trim())) return;
    setCapabilityLoading(true);
    try {
      const body = { column: selectedColumn, dataset_id: selectedDataset.dataset_id };
      if (uslInput.trim()) body.usl = parseFloat(uslInput);
      if (lslInput.trim()) body.lsl = parseFloat(lslInput);
      body.sigma_shift = sigmaShift;
      if (selectedDerivedMetric) body.derived_metric_id = selectedDerivedMetric.metric_id;
      const response = await fetch(`${API}/projects/${projectId}/analyze/capability`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Capability analysis failed"); }
      const data = await response.json();
      setCapabilityResult(data.capability || null);
      setPerformanceResult(data.performance || null);
      setFinancialResult(null);
      if (data.capability?.eligible) toast.success("Capability analysis complete");
      else toast.info(data.capability?.reason || "Not eligible for capability analysis");
      saveMeasureSettings();
    } catch (error) { toast.error(error.message); } finally { setCapabilityLoading(false); }
  };

  const runFinancialImpact = async () => {
    if (!annualVolume.trim() || !costPerDefect.trim()) return;
    setFinancialLoading(true);
    try {
      const body = {
        column: selectedColumn, dataset_id: selectedDataset.dataset_id,
        annual_volume: parseInt(annualVolume), cost_per_defect: parseFloat(costPerDefect),
        use_long_term: useLongTerm,
      };
      if (uslInput.trim()) body.usl = parseFloat(uslInput);
      if (lslInput.trim()) body.lsl = parseFloat(lslInput);
      if (targetCpk.trim()) body.target_cpk = parseFloat(targetCpk);
      body.sigma_shift = sigmaShift;
      if (selectedDerivedMetric) body.derived_metric_id = selectedDerivedMetric.metric_id;
      const response = await fetch(`${API}/projects/${projectId}/analyze/financial-impact`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "Financial impact failed"); }
      const data = await response.json();
      setFinancialResult(data);
      if (data.eligible) toast.success("Financial impact projected");
      saveMeasureSettings();
    } catch (error) { toast.error(error.message); } finally { setFinancialLoading(false); }
  };

  const deleteDerivedMetric = async (metricId) => {
    try {
      const res = await fetch(`${API}/projects/${projectId}/datasets/${selectedDataset.dataset_id}/derived-metrics/${metricId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        toast.success("Derived metric deleted");
        fetchDerivedMetrics(selectedDataset.dataset_id);
        if (selectedDerivedMetric?.metric_id === metricId) { setSelectedColumn(""); setSelectedDerivedMetric(null); setColumnClassification(null); }
      }
    } catch { toast.error("Failed to delete metric"); }
  };

  const exportDataset = async () => {
    if (!selectedDataset) return;
    try {
      const response = await fetch(`${API}/projects/${projectId}/export/data/${selectedDataset.dataset_id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error("Export failed");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = selectedDataset.filename;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      toast.success("Dataset exported");
    } catch (error) { toast.error(error.message); }
  };

  const getNumericColumns = () => {
    if (selectedDataset?.profile?.columns) {
      return Object.entries(selectedDataset.profile.columns)
        .filter(([, info]) => info.is_numeric || (info.dtype && (info.dtype.includes("int") || info.dtype.includes("float"))))
        .map(([name]) => name);
    }
    return selectedDataset?.columns || [];
  };

  // ---- AI helpers ----
  const measureAiCall = async (endpoint, body, setResult, label) => {
    setAiGenerating(label);
    try {
      const response = await fetch(`${API}/ai/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId, dataset_id: selectedDataset?.dataset_id, ...body }),
      });
      if (!response.ok) { const err = await response.json(); throw new Error(err.detail || "AI generation failed"); }
      const data = await response.json();
      if (data.ai_unavailable) { toast.error("AI service unavailable."); return; }
      setResult(data);
      toast.success(`${label} generated!`);
    } catch (error) { toast.error(error.message); } finally { setAiGenerating(""); }
  };

  const generateDataCollectionPlan = () => measureAiCall("suggest-data-collection-plan", {}, setAiDataCollectionPlan, "Data Collection Plan");
  const reviewMeasurementSystem = () => measureAiCall("review-measurement-system", {}, setAiMeasurementReview, "Measurement Review");
  const suggestBaselineMetrics = () => measureAiCall("suggest-baseline-metrics", {}, setAiBaselineSuggestions, "Baseline Suggestions");

  if (loading) return <div className="text-muted-foreground">Loading Measure phase...</div>;

  return (
    <div className="space-y-6" data-testid="measure-phase">
      <div>
        <h1 className="font-heading text-2xl font-bold">Measure Phase</h1>
        <p className="text-muted-foreground">Establish baseline metrics and validate measurement systems</p>
      </div>

      <Tabs defaultValue="data" className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="data" className="gap-2" data-testid="tab-data"><FileSpreadsheet className="w-4 h-4" /> Data Upload</TabsTrigger>
          <TabsTrigger value="baseline" className="gap-2" data-testid="tab-baseline"><TrendingUp className="w-4 h-4" /> Baseline</TabsTrigger>
          <TabsTrigger value="control" className="gap-2" data-testid="tab-control"><BarChart3 className="w-4 h-4" /> Control Chart</TabsTrigger>
        </TabsList>

        <TabsContent value="data" className="space-y-6">
          <DataUploadTab
            projectId={projectId} token={token}
            datasets={datasets} selectedDataset={selectedDataset} setSelectedDataset={setSelectedDataset}
            uploading={uploading} handleFileUpload={handleFileUpload} exportDataset={exportDataset}
            derivedMetrics={derivedMetrics} showCreateDerivedModal={showCreateDerivedModal} setShowCreateDerivedModal={setShowCreateDerivedModal}
            fetchDerivedMetrics={fetchDerivedMetrics} deleteDerivedMetric={deleteDerivedMetric}
            aiGenerating={aiGenerating} aiDataCollectionPlan={aiDataCollectionPlan} aiMeasurementReview={aiMeasurementReview}
            reviewMeasurementSystem={reviewMeasurementSystem} generateDataCollectionPlan={generateDataCollectionPlan}
          />
        </TabsContent>

        <TabsContent value="baseline" className="space-y-6">
          <BaselineTab
            projectId={projectId} token={token}
            datasets={datasets} selectedDataset={selectedDataset} setSelectedDataset={setSelectedDataset}
            selectedColumn={selectedColumn} setSelectedColumn={setSelectedColumn}
            derivedMetrics={derivedMetrics} selectedDerivedMetric={selectedDerivedMetric} setSelectedDerivedMetric={setSelectedDerivedMetric}
            baseline={baseline} setBaseline={setBaseline}
            analyzing={analyzing} calculateBaseline={calculateBaseline}
            fetchDerivedMetrics={fetchDerivedMetrics} getNumericColumns={getNumericColumns}
            aiGenerating={aiGenerating} aiBaselineSuggestions={aiBaselineSuggestions} suggestBaselineMetrics={suggestBaselineMetrics}
          />
        </TabsContent>

        <TabsContent value="control" className="space-y-6">
          <ControlChartTab
            datasets={datasets} selectedDataset={selectedDataset} setSelectedDataset={setSelectedDataset}
            selectedColumn={selectedColumn} setSelectedColumn={setSelectedColumn}
            derivedMetrics={derivedMetrics} selectedDerivedMetric={selectedDerivedMetric} setSelectedDerivedMetric={setSelectedDerivedMetric}
            columnClassification={columnClassification} setColumnClassification={setColumnClassification}
            denominatorColumn={denominatorColumn} setDenominatorColumn={setDenominatorColumn}
            controlChart={controlChart} setControlChart={setControlChart}
            analyzing={analyzing} generateControlChart={generateControlChart}
            classifyColumn={classifyColumn} fetchDerivedMetrics={fetchDerivedMetrics} getNumericColumns={getNumericColumns}
            capabilityOpen={capabilityOpen} setCapabilityOpen={setCapabilityOpen}
            uslInput={uslInput} setUslInput={setUslInput} lslInput={lslInput} setLslInput={setLslInput}
            capabilityLoading={capabilityLoading} capabilityResult={capabilityResult} performanceResult={performanceResult}
            sigmaShift={sigmaShift} setSigmaShift={setSigmaShift} runCapabilityAnalysis={runCapabilityAnalysis}
            annualVolume={annualVolume} setAnnualVolume={setAnnualVolume}
            costPerDefect={costPerDefect} setCostPerDefect={setCostPerDefect}
            targetCpk={targetCpk} setTargetCpk={setTargetCpk}
            useLongTerm={useLongTerm} setUseLongTerm={setUseLongTerm}
            financialLoading={financialLoading} financialResult={financialResult}
            runFinancialImpact={runFinancialImpact}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
